import { BaseElement } from '../../../base-element';
import type { ContactFields, ValidationError } from '../../../../state/form.state';
import type { SelectOption } from '../../../ui/dt-select';
import '../../../ui/dt-input';
import '../../../ui/dt-select';
import '../../../ui/dt-card';

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

export interface ContactSectionChangeDetail {
  section: 'contact';
  field: string;
  path: string;
  value: string;
}

const states: SelectOption[] = [
  { value: 'AL', label: 'AL — Alabama' },
  { value: 'AK', label: 'AK — Alaska' },
  { value: 'AZ', label: 'AZ — Arizona' },
  { value: 'AR', label: 'AR — Arkansas' },
  { value: 'CA', label: 'CA — California' },
  { value: 'CO', label: 'CO — Colorado' },
  { value: 'CT', label: 'CT — Connecticut' },
  { value: 'DE', label: 'DE — Delaware' },
  { value: 'FL', label: 'FL — Florida' },
  { value: 'GA', label: 'GA — Georgia' },
  { value: 'HI', label: 'HI — Hawaii' },
  { value: 'ID', label: 'ID — Idaho' },
  { value: 'IL', label: 'IL — Illinois' },
  { value: 'IN', label: 'IN — Indiana' },
  { value: 'IA', label: 'IA — Iowa' },
  { value: 'KS', label: 'KS — Kansas' },
  { value: 'KY', label: 'KY — Kentucky' },
  { value: 'LA', label: 'LA — Louisiana' },
  { value: 'ME', label: 'ME — Maine' },
  { value: 'MD', label: 'MD — Maryland' },
  { value: 'MA', label: 'MA — Massachusetts' },
  { value: 'MI', label: 'MI — Michigan' },
  { value: 'MN', label: 'MN — Minnesota' },
  { value: 'MS', label: 'MS — Mississippi' },
  { value: 'MO', label: 'MO — Missouri' },
  { value: 'MT', label: 'MT — Montana' },
  { value: 'NE', label: 'NE — Nebraska' },
  { value: 'NV', label: 'NV — Nevada' },
  { value: 'NH', label: 'NH — New Hampshire' },
  { value: 'NJ', label: 'NJ — New Jersey' },
  { value: 'NM', label: 'NM — New Mexico' },
  { value: 'NY', label: 'NY — New York' },
  { value: 'NC', label: 'NC — North Carolina' },
  { value: 'ND', label: 'ND — North Dakota' },
  { value: 'OH', label: 'OH — Ohio' },
  { value: 'OK', label: 'OK — Oklahoma' },
  { value: 'OR', label: 'OR — Oregon' },
  { value: 'PA', label: 'PA — Pennsylvania' },
  { value: 'RI', label: 'RI — Rhode Island' },
  { value: 'SC', label: 'SC — South Carolina' },
  { value: 'SD', label: 'SD — South Dakota' },
  { value: 'TN', label: 'TN — Tennessee' },
  { value: 'TX', label: 'TX — Texas' },
  { value: 'UT', label: 'UT — Utah' },
  { value: 'VT', label: 'VT — Vermont' },
  { value: 'VA', label: 'VA — Virginia' },
  { value: 'WA', label: 'WA — Washington' },
  { value: 'WV', label: 'WV — West Virginia' },
  { value: 'WI', label: 'WI — Wisconsin' },
  { value: 'WY', label: 'WY — Wyoming' },
];

export class ContactSection extends BaseElement {
  private _contact: ContactFields | null = null;
  private _errors: ValidationError[] = [];

  get contact(): ContactFields | null {
    return this._contact;
  }

  set contact(value: ContactFields | null) {
    this._contact = value;
    this.updateRender();
  }

  get errors(): ValidationError[] {
    return this._errors;
  }

  set errors(value: ValidationError[]) {
    this._errors = value ?? [];
    this.updateRender();
  }

  connectedCallback(): void {
    this.updateRender();
  }

  private getError(path: string): string {
    return this._errors.find((error) => error.field === path)?.message ?? '';
  }

  private emitChange(field: string, value: string): void {
    const path = `contact.${field}`;
    this.emit<ContactSectionChangeDetail>('section-change', {
      section: 'contact',
      field,
      path,
      value,
    });
  }

  private emitBlur(field: string): void {
    const path = `contact.${field}`;
    this.emit('section-blur', { section: 'contact', field, path });
  }

  private attachEventListeners(): void {
    this.$$('dt-input[data-field]').forEach((input) => {
      const field = input.getAttribute('data-field');
      if (!field) return;
      input.addEventListener('input', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange(field, detail?.value ?? '');
      });
      input.addEventListener('blur', () => this.emitBlur(field));
    });

    const stateSelect = this.$('dt-select[data-field="ownerAddress.state"]') as
      | HTMLElement
      | null;
    if (stateSelect) {
      (stateSelect as any).options = states;
      stateSelect.addEventListener('change', (event: Event) => {
        const detail = (event as CustomEvent<{ value: string }>).detail;
        this.emitChange('ownerAddress.state', detail?.value ?? '');
        this.emitBlur('ownerAddress.state');
      });
    }
  }

  private updateRender(): void {
    const contact = this._contact;

    this.render(`
      <section class="flex flex-col gap-3" data-testid="contact-section">
        <div class="text-sm font-semibold uppercase tracking-widest text-neutral-600">Owner / Handler Information</div>
        <dt-card variant="outlined">
          <div class="grid gap-4 md:grid-cols-3">
            <div class="md:col-span-3" data-control-path="contact.owners">
              <dt-input
                label="Owner(s)"
                value="${escapeHtml(contact?.owners ?? '')}"
                required
                error="${escapeHtml(this.getError('contact.owners'))}"
                data-field="owners"
                data-path="contact.owners"
              ></dt-input>
            </div>
            <div class="md:col-span-3 rounded-lg border border-neutral-200 bg-neutral-50 p-4" data-control-path="contact.ownerAddress">
              <div class="text-xs font-semibold uppercase tracking-widest text-neutral-500">Owner Address</div>
              <div class="mt-3 grid gap-4 md:grid-cols-3">
                <div class="md:col-span-3" data-control-path="contact.ownerAddress.street">
                  <dt-input
                    label="Street"
                    value="${escapeHtml(contact?.ownerAddress.street ?? '')}"
                    required
                    error="${escapeHtml(this.getError('contact.ownerAddress.street'))}"
                    data-field="ownerAddress.street"
                    data-path="contact.ownerAddress.street"
                  ></dt-input>
                </div>
                <div data-control-path="contact.ownerAddress.city">
                  <dt-input
                    label="City"
                    value="${escapeHtml(contact?.ownerAddress.city ?? '')}"
                    required
                    error="${escapeHtml(this.getError('contact.ownerAddress.city'))}"
                    data-field="ownerAddress.city"
                    data-path="contact.ownerAddress.city"
                  ></dt-input>
                </div>
                <div data-control-path="contact.ownerAddress.state">
                  <dt-select
                    label="State"
                    placeholder="Select state"
                    value="${escapeHtml(contact?.ownerAddress.state ?? '')}"
                    required
                    error="${escapeHtml(this.getError('contact.ownerAddress.state'))}"
                    data-field="ownerAddress.state"
                    data-path="contact.ownerAddress.state"
                  ></dt-select>
                </div>
                <div data-control-path="contact.ownerAddress.zip">
                  <dt-input
                    label="ZIP"
                    value="${escapeHtml(contact?.ownerAddress.zip ?? '')}"
                    required
                    error="${escapeHtml(this.getError('contact.ownerAddress.zip'))}"
                    data-field="ownerAddress.zip"
                    data-path="contact.ownerAddress.zip"
                  ></dt-input>
                </div>
              </div>
            </div>
            <div class="md:col-span-2" data-control-path="contact.email">
              <dt-input
                label="Email"
                type="email"
                value="${escapeHtml(contact?.email ?? '')}"
                required
                error="${escapeHtml(this.getError('contact.email'))}"
                data-field="email"
                data-path="contact.email"
              ></dt-input>
            </div>
            <div data-control-path="contact.phone">
              <dt-input
                label="Phone"
                type="tel"
                value="${escapeHtml(contact?.phone ?? '')}"
                required
                error="${escapeHtml(this.getError('contact.phone'))}"
                data-field="phone"
                data-path="contact.phone"
              ></dt-input>
            </div>
            <div class="md:col-span-3" data-control-path="contact.handler">
              <dt-input
                label="Handler (if different from owner)"
                value="${escapeHtml(contact?.handler ?? '')}"
                data-field="handler"
                data-path="contact.handler"
              ></dt-input>
            </div>
            <div data-control-path="contact.membershipNumber">
              <dt-input
                label="ASCA Membership Number"
                value="${escapeHtml(contact?.membershipNumber ?? '')}"
                data-field="membershipNumber"
                data-path="contact.membershipNumber"
              ></dt-input>
            </div>
            <div class="md:col-span-2 rounded-lg border border-neutral-200 bg-neutral-50 p-4" data-control-path="contact.junior">
              <div class="text-xs font-semibold uppercase tracking-widest text-neutral-500">Junior Handler (if applicable)</div>
              <div class="mt-3 grid gap-4 md:grid-cols-2">
                <div data-control-path="contact.junior.dob">
                  <dt-input
                    label="Junior DOB"
                    type="date"
                    value="${escapeHtml(contact?.junior.dob ?? '')}"
                    data-field="junior.dob"
                    data-path="contact.junior.dob"
                  ></dt-input>
                </div>
                <div data-control-path="contact.junior.memberId">
                  <dt-input
                    label="ASCA Junior Member Number"
                    value="${escapeHtml(contact?.junior.memberId ?? '')}"
                    data-field="junior.memberId"
                    data-path="contact.junior.memberId"
                  ></dt-input>
                </div>
              </div>
            </div>
          </div>
        </dt-card>
      </section>
    `);

    this.attachEventListeners();
  }
}

customElements.define('dt-contact-section', ContactSection);
